import * as layout from '../../components/markLayout.module.css';
import { MarkButton, MarkConfirmActions } from '../../components/MarkControls';
import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import MarkModal from '../../components/MarkModal';
import useBlastDetail from '../../api/useBlastDetail';
import useUpdateBlast from '../../api/useUpdateBlast';
import useUnsavedChangesRegistration from '../../hooks/useUnsavedChangesRegistration';
import Step2Audience from './New/Step2Audience';
import VariantBuilder from './New/VariantBuilder';
import type { Step2Data } from './New/Step2Audience';
import type { BlastDetail, BlastVariantInput } from '../../types';
import {
    extractBlastErrorMessage,
    getScheduleError,
    getLocalSendTimeError,
    toDatetimeLocal,
    toUtcIso
} from './blastFormUtils';
const toFormData = (blast: BlastDetail): Step2Data => ({
    segmentId: blast.segment_id,
    segmentName: blast.segment_name,
    accountRecipients: blast.account_recipients ?? 'ALL',
    fromAddress: blast.from_address,
    scheduleMode: blast.schedule_mode,
    scheduledAt: toDatetimeLocal(blast.scheduled_at),
    localSendTime: blast.local_send_time ?? ''
});
const toVariants = (blast: BlastDetail): BlastVariantInput[] =>
    blast.variants.map(({ label, ratio }) => ({ label, ratio }));
const variantSnapshot = (variants: BlastVariantInput[]): string =>
    JSON.stringify(variants.map(({ label, ratio }) => ({ label: label.trim(), ratio })));
const persistentSnapshot = (data: Step2Data, variants: BlastVariantInput[]): string =>
    JSON.stringify({
        segmentId: data.segmentId,
        accountRecipients: data.accountRecipients,
        fromAddress: data.fromAddress,
        scheduleMode: data.scheduleMode,
        scheduledAt: data.scheduledAt,
        localSendTime: data.localSendTime,
        variants: variants.map(({ label, ratio }) => ({ label: label.trim(), ratio }))
    });
const BlastEditForm: React.FC<{
    blast: BlastDetail;
}> = ({ blast }) => {
    const navigate = useNavigate();
    const updateBlast = useUpdateBlast();
    const [formData, setFormData] = useState<Step2Data>(() => toFormData(blast));
    const [variants, setVariants] = useState<BlastVariantInput[]>(() => toVariants(blast));
    const initialSnapshot = useRef(persistentSnapshot(formData, variants));
    const initialVariantsSnapshot = useRef(variantSnapshot(variants));
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [showVariantDeleteConfirm, setShowVariantDeleteConfirm] = useState(false);
    const dirty = persistentSnapshot(formData, variants) !== initialSnapshot.current;
    const variantsDirty = variantSnapshot(variants) !== initialVariantsSnapshot.current;
    const removedVariantLabels = blast.variants
        .filter((original) => !variants.some((variant) => variant.label.trim() === original.label))
        .map((variant) => variant.label);
    const isSaving = updateBlast.isPending;
    const unregister = useUnsavedChangesRegistration('blast-edit', dirty, isSaving);
    const handleChange = useCallback((data: Step2Data) => {
        setFormData(data);
        setSubmitError(null);
    }, []);
    const handleVariantsChange = useCallback((nextVariants: BlastVariantInput[]) => {
        setVariants(nextVariants);
        setSubmitError(null);
    }, []);
    const scheduleError =
        formData.scheduleMode === 'scheduled'
            ? getScheduleError(formData.scheduledAt) ||
              getLocalSendTimeError(formData.localSendTime)
            : null;
    const save = () => {
        if (isSaving) return;
        setShowVariantDeleteConfirm(false);
        if (
            formData.scheduleMode === 'scheduled' &&
            (getScheduleError(formData.scheduledAt) ||
                getLocalSendTimeError(formData.localSendTime))
        ) {
            setSubmitError('Review the schedule fields before saving.');
            return;
        }
        updateBlast.mutate(
            {
                blast_id: blast.blast_id,
                ...(variantsDirty && {
                    variants: variants.map((variant) => ({
                        label: variant.label.trim(),
                        ratio: variant.ratio
                    }))
                }),
                segment_id: formData.segmentId,
                ...(blast.delivery_scope === 'PER_ACCOUNT' && {
                    account_recipients: formData.accountRecipients
                }),
                from_address: formData.fromAddress.trim(),
                schedule_mode: formData.scheduleMode,
                ...(formData.scheduleMode === 'scheduled'
                    ? {
                          scheduled_at: formData.scheduledAt
                              ? toUtcIso(formData.scheduledAt)
                              : null,
                          local_send_time: formData.localSendTime || null
                      }
                    : {
                          scheduled_at: null,
                          local_send_time: null
                      })
            },
            {
                onSuccess: () => {
                    flushSync(unregister);
                    navigate(`/mark/blasts/${blast.blast_id}`);
                },
                onError: (error) => setSubmitError(extractBlastErrorMessage(error))
            }
        );
    };
    const handleSave = () => {
        if (!dirty || isSaving) return;
        if (scheduleError) {
            setSubmitError(scheduleError);
            return;
        }
        if (variants.length === 0) {
            setSubmitError('At least one variant is required.');
            return;
        }
        if (variants.some((variant) => !variant.label.trim())) {
            setSubmitError('Every variant needs a label.');
            return;
        }
        const normalizedLabels = variants.map((variant) => variant.label.trim().toLowerCase());
        if (new Set(normalizedLabels).size !== normalizedLabels.length) {
            setSubmitError('Variant labels must be unique.');
            return;
        }
        if (variants.reduce((sum, variant) => sum + variant.ratio, 0) !== 100) {
            setSubmitError('Variant ratios must sum to 100.');
            return;
        }
        setSubmitError(null);
        if (removedVariantLabels.length > 0) {
            setShowVariantDeleteConfirm(true);
            return;
        }
        save();
    };
    return (
        <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
            <H role="h5">{`Edit: ${blast.name}`}</H>
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    marginBottom: '1.2rem'
                }}
            >
                Name, message class, and delivery scope are locked after create. Variants, audience,
                and schedule can change while this blast is a draft.
            </P>
            <fieldset
                disabled={isSaving}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                <VariantBuilder variants={variants} onChange={handleVariantsChange} />
                {removedVariantLabels.length > 0 && (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-warning)',
                            margin: '0.8rem 0 1.2rem'
                        }}
                    >
                        Saving will delete the templates for removed or renamed variants:{' '}
                        {removedVariantLabels.join(', ')}.
                    </P>
                )}
                <div
                    style={{
                        marginTop: '1.6rem'
                    }}
                >
                    <Step2Audience
                        data={formData}
                        onChange={handleChange}
                        deliveryScope={blast.delivery_scope}
                        errors={[]}
                        showErrors={!!submitError}
                    />
                </div>
            </fieldset>
            {submitError && (
                <div
                    role="alert"
                    style={{
                        marginTop: '1rem'
                    }}
                    className={layout.notice}
                >
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-danger)'
                        }}
                    >
                        {submitError}
                    </P>
                </div>
            )}
            <div
                style={{
                    marginTop: '1.6rem'
                }}
                className={layout.actions}
            >
                <MarkButton
                    type="button"
                    onClick={handleSave}
                    disabled={!dirty || isSaving}
                    variant="primary"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </MarkButton>
                <MarkButton
                    type="button"
                    onClick={() => navigate(`/mark/blasts/${blast.blast_id}`)}
                    disabled={isSaving}
                >
                    Cancel
                </MarkButton>
            </div>
            {showVariantDeleteConfirm && (
                <MarkModal
                    aria-label="Delete variant templates?"
                    onClose={() => setShowVariantDeleteConfirm(false)}
                    isPending={isSaving}
                    type="warn"
                    styles={{ width: 'min(64rem, 94vw)' }}
                >
                    <H role="h4">Delete variant templates?</H>
                    <P>
                        {`Saving will permanently delete the templates for: ${removedVariantLabels.join(', ')}.`}
                    </P>
                    <MarkConfirmActions
                        confirmLabel="Save and delete"
                        cancelLabel="Keep editing"
                        onConfirm={save}
                        onCancel={() => setShowVariantDeleteConfirm(false)}
                        disabled={isSaving}
                        cancelDisabled={isSaving}
                    />
                </MarkModal>
            )}
        </Section>
    );
};
const EditBlast: React.FC = () => {
    const { blastId } = useParams<{
        blastId: string;
    }>();
    const numericId = Number(blastId);
    const { data: blastResponse, isLoading, error } = useBlastDetail(numericId);
    const blast = blastResponse?.data;
    const isNonDraft = !!blast && blast.status !== 'DRAFT';
    if (isLoading) return <InlineLoader />;
    if (error) return <QueryInlineError error={error} />;
    if (!blast) return <P>Blast not found.</P>;
    if (isNonDraft) return <Navigate to={`/mark/blasts/${numericId}`} replace />;
    return <BlastEditForm key={blast.blast_id} blast={blast} />;
};
export default EditBlast;
